import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import AppModal from "./AppModal";
import {
  getCompany,
  clearCompany,
  onCompanyChange,
} from "../../util/accounting/company";

/**
 * The side menu while you are inside accounting.
 *
 * Accounting is a system within the system: entering it replaces the whole side
 * menu rather than adding to it, so what is in front of you is only the work you
 * are actually doing. The way back out is the first item, because a mode you
 * cannot obviously leave feels like a trap.
 *
 * Markup deliberately uses this system's own sidebar classes, not the ones the
 * pages arrived with, so accounting reads as part of the building rather than
 * something bolted on.
 */

/** Where the menu can take you. `end` marks links that must match exactly. */
export const ACCOUNTING_MENU = [
  { heading: "Overview" },
  { to: "/accounting", icon: "fa-solid fa-gauge", name: "Dashboard", end: true },

  { heading: "Transactions" },
  { to: "/accounting/journal", icon: "fa-solid fa-book", name: "Journal Entries" },
  { to: "/accounting/journal/contra", icon: "fa-solid fa-right-left", name: "Contra Entries" },
  { to: "/accounting/vouchers", icon: "fa-solid fa-receipt", name: "Vouchers" },
  { to: "/accounting/invoices?type=outgoing", icon: "fa-solid fa-file-invoice", name: "Sales Invoices" },
  { to: "/accounting/invoices?type=incoming", icon: "fa-solid fa-file-invoice-dollar", name: "Purchase Invoices" },
  { to: "/accounting/contacts", icon: "fa-solid fa-address-book", name: "Customers & Suppliers" },

  { heading: "Ledger & Reports" },
  { to: "/accounting/accounts", icon: "fa-solid fa-list-check", name: "Chart of Accounts" },
  { to: "/accounting/ledger", icon: "fa-solid fa-table-list", name: "General Ledger" },
  { to: "/accounting/reports/trial-balance", icon: "fa-solid fa-scale-balanced", name: "Trial Balance" },
  { to: "/accounting/reports/income-statement", icon: "fa-solid fa-chart-simple", name: "Income Statement" },
  { to: "/accounting/reports/balance-sheet", icon: "fa-solid fa-building-columns", name: "Balance Sheet" },
  { to: "/accounting/reports/cash-flow", icon: "fa-solid fa-money-bill-transfer", name: "Cash Flow" },

  { heading: "Assets & Liabilities" },
  { to: "/accounting/assets", icon: "fa-solid fa-building", name: "Fixed Assets" },
  { to: "/accounting/liabilities", icon: "fa-solid fa-scale-unbalanced", name: "Liabilities" },
  { to: "/accounting/liabilities?tab=borrowings", icon: "fa-solid fa-hand-holding-dollar", name: "Borrowed Capital" },

  { heading: "Operations" },
  { to: "/accounting/payroll", icon: "fa-solid fa-money-check-dollar", name: "Payroll" },
  { to: "/accounting/payroll/advances", icon: "fa-solid fa-sack-dollar", name: "Staff Advances" },
  { to: "/accounting/budget", icon: "fa-solid fa-chart-pie", name: "Budgets" },
  { to: "/accounting/projects", icon: "fa-solid fa-diagram-project", name: "Cost Centres" },
  { to: "/accounting/bank/reconcile", icon: "fa-solid fa-building-columns", name: "Bank Reconciliation" },
  // Both were routed and working with nothing anywhere linking to them.
  { to: "/accounting/bank/manual-reconcile", icon: "fa-solid fa-list-check", name: "Manual Reconciliation" },
  { to: "/accounting/bank/import", icon: "fa-solid fa-file-arrow-up", name: "Import Statement" },

  { heading: "Administration" },
  { to: "/accounting/approvals", icon: "fa-solid fa-circle-check", name: "Approvals" },
  { to: "/accounting/financial-year", icon: "fa-solid fa-calendar", name: "Financial Years" },
  // Closing a year was built, routed and tested, but nothing in the running
  // application ever linked to it — the only reference anywhere was its own
  // test file. Same for the three below it.
  { to: "/accounting/year-close", icon: "fa-solid fa-calendar-check", name: "Year Close" },
  { to: "/accounting/config", icon: "fa-solid fa-gear", name: "Settings" },
  { to: "/accounting/audit", icon: "fa-solid fa-clipboard-list", name: "Audit Trail" },
  { to: "/accounting/records", icon: "fa-solid fa-box-archive", name: "Record Keeper" },
  { to: "/accounting/migration", icon: "fa-solid fa-file-import", name: "Migration Assistant" },
  { to: "/accounting/guide", icon: "fa-solid fa-circle-question", name: "User Guide" },
];

/** The open company, set apart from the links so it reads as a label. */
const companyStyle = {
  padding: "14px 20px 12px",
  lineHeight: 1.35,
  borderBottom: "1px solid rgba(255,255,255,0.08)",
};

const headingStyle = {
  padding: "14px 20px 6px",
  fontSize: "10.5px",
  letterSpacing: "0.09em",
  textTransform: "uppercase",
  opacity: 0.55,
};

export default function AccountingMenu() {
  const location = useLocation();
  const navigate = useNavigate();
  const [company, setCompany] = useState(getCompany);
  // Which exit is being confirmed: "company" (back to the picker) or "system"
  // (out of accounting altogether). Null while nothing is being asked.
  const [pendingExit, setPendingExit] = useState(null);

  useEffect(() => onCompanyChange(() => setCompany(getCompany())), []);

  /**
   * Both exits close the open company.
   *
   * Leaving accounting is leaving the company too: walking out of the room does
   * not leave the safe open behind you, and coming back should ask again which
   * books you want rather than silently resuming the last ones — the wrong
   * company is the one mistake here that is expensive to undo.
   *
   * Both are asked about first. Not because either destroys anything — neither
   * does — but because these sit a few pixels from the pages people use all
   * day, and losing what is on screen mid-task is a jarring way to discover you
   * misclicked.
   */
  const confirmedExit = () => {
    const target = pendingExit === "system" ? "/" : "/accounting";
    setPendingExit(null);
    clearCompany();
    navigate(target);
  };

  const isActive = (item) => {
    const [path] = item.to.split("?");
    return item.end
      ? location.pathname === path
      : location.pathname === path || location.pathname.startsWith(path + "/");
  };

  return (
    <>
      {/* Whose books these are. Every figure below belongs to this company and
          nothing else, so it is named before anything else is shown. */}
      {company && (
        <li className="nav-item" style={companyStyle}>
          <span
            className="sidemenu-label d-block text-truncate"
            style={{ fontWeight: 600 }}
            title={company.name}
          >
            {company.name}
          </span>
          <span
            className="sidemenu-label d-block"
            style={{ fontSize: 10.5, opacity: 0.6 }}
          >
            {company.currency || "UGX"} &bull; open
          </span>
        </li>
      )}

      {/* The ways out, first and unmissable. */}
      <li className="nav-item">
        <button
          type="button"
          className="nav-link w-100 text-start border-0 bg-transparent"
          style={{ cursor: "pointer" }}
          onClick={() => setPendingExit("company")}
          title="Close these books and choose another company"
        >
          <i className="fa-solid fa-right-from-bracket" />
          <span className="sidemenu-label">Exit Company</span>
        </button>
      </li>

      <li className="nav-item">
        <button
          type="button"
          className="nav-link w-100 text-start border-0 bg-transparent"
          style={{ cursor: "pointer" }}
          onClick={() => setPendingExit("system")}
          title="Close these books and return to the main system"
        >
          <i className="fa-solid fa-arrow-left-long" />
          <span className="sidemenu-label">Exit Accounting</span>
        </button>
      </li>

      {pendingExit && (
        <AppModal
          show
          onClose={() => setPendingExit(null)}
          title="Close these books?"
          icon="fa-solid fa-right-from-bracket"
          footer={
            <>
              <button
                type="button"
                className="btn btn-light"
                onClick={() => setPendingExit(null)}
              >
                Stay here
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmedExit}
              >
                {pendingExit === "system" ? "Exit accounting" : "Exit company"}
              </button>
            </>
          }
        >
          <p className="mb-2">
            You are about to close{" "}
            <strong>{company?.name || "this company"}</strong>
            {pendingExit === "system"
              ? " and leave accounting."
              : " and go back to choosing a company."}
          </p>
          <p className="mb-0 text-muted" style={{ fontSize: 13 }}>
            {pendingExit === "system"
              ? "Coming back into accounting will ask which company to open. Anything you have typed but not saved on this page will be lost."
              : "Anything you have typed but not saved on this page will be lost."}
          </p>
        </AppModal>
      )}

      {ACCOUNTING_MENU.map((item, i) =>
        item.heading ? (
          <li className="nav-item" key={`h-${i}`} style={headingStyle}>
            {item.heading}
          </li>
        ) : (
          <li
            className={`nav-item${isActive(item) ? " show" : ""}`}
            key={item.to}
          >
            <Link className="nav-link" to={item.to}>
              <i className={item.icon} />
              <span className="sidemenu-label">{item.name}</span>
            </Link>
          </li>
        )
      )}
    </>
  );
}
